import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { api, isApiError } from '../../api';
import { Badge } from '../../components/Badge';
import { Callout, Section } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { DomainLink } from '../../components/Ids';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { CopyText, Loading } from '../../components/States';
import { formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { PollMessageActions } from '../operator/RegistryMarket';
import { PayloadBlock, RefreshButton, RegistryRef, useRegistryCrumbs } from './shared';

/** `/registry/poll/:id`: one poll message, its objects and its redacted content (spec §29). */
export function PollMessagePage() {
  const { t } = useTranslation('registry');
  const { id = '' } = useParams();
  const result = useApi((signal) => api.getPollMessage(id, { signal }), [id]);
  const label = result.status === 'success' ? result.data.registry_message_id : t('pollMessage.title');
  useDocumentTitle(result.status === 'success' ? `${t('pollMessage.title')} ${result.data.registry_message_id}` : t('pollMessage.title'));
  const crumbs = useRegistryCrumbs({ label: t('poll.title'), to: '/registry/poll' }, { label });

  if (result.status === 'loading') {
    return (
      <>
        <PageHeader title={t('pollMessage.title')} crumbs={crumbs} />
        <Loading rows={3} />
      </>
    );
  }
  if (result.status === 'error') {
    const notFound = isApiError(result.error) && result.error.status === 404;
    return (
      <>
        <PageHeader title={t('pollMessage.title')} crumbs={crumbs} />
        <DataError error={result.error} onRetry={notFound ? undefined : result.reload} title={notFound ? t('pollMessage.notFound') : t('pollMessage.loadError')} />
      </>
    );
  }

  const p = result.data;
  return (
    <>
      <PageHeader
        title={p.message ?? t('pollMessage.title')}
        crumbs={crumbs}
        subtitle={t('pollMessage.subtitle')}
        actions={
          <>
            {p.processed_at ? <Badge tone="ok">{t('pollMessage.handled')}</Badge> : <Badge tone="warn">{t('pollMessage.notHandled')}</Badge>}
            <RefreshButton onClick={result.reload} />
            <PollMessageActions message={p} onChanged={result.reload} />
          </>
        }
      />
      <div className="grid gap-6 [&>*]:min-w-0 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Section title={t('pollMessage.details')}>
          <KeyValue
            columns={2}
            rows={[
              [t('poll.registry'), <RegistryRef key="r" name={p.registry_name} code={p.registry_code} />],
              [t('poll.messageId'), <CopyText key="m" value={p.registry_message_id} />],
              [t('poll.queuedAt'), formatDateTime(p.queued_at)],
              [t('poll.received'), formatDateTime(p.received_at)],
              [t('poll.acked'), p.acked_at ? formatDateTime(p.acked_at) : <Badge key="a" tone="warn">{t('poll.no')}</Badge>],
              [t('poll.processed'), p.processed_at ? formatDateTime(p.processed_at) : <Badge key="p" tone="pending">{t('poll.pending')}</Badge>],
              [t('pollMessage.id'), <CopyText key="id" value={p.id} />],
            ]}
          />
          {p.processing_error ? (
            <div className="mt-4" role="alert">
              <Callout tone="crit" title={t('pollMessage.processingError')}>
                {p.processing_error}
              </Callout>
            </div>
          ) : null}
        </Section>

        <Section title={t('pollMessage.linked')}>
          <KeyValue
            rows={[
              [t('reconciliation.domain'), p.domain_name ? <DomainLink key="d" keyOrName={p.domain_name} /> : '—'],
              [t('pollMessage.objectName'), p.object_name ? <code key="o" className="text-xs">{p.object_name}</code> : '—'],
            ]}
          />
        </Section>

        <Section title={t('pollMessage.content')} className="xl:col-span-2">
          {p.payload_visible ? (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">{t('pollMessage.contentNote')}</p>
              <PayloadBlock label={t('pollMessage.content')} value={p.payload} />
            </div>
          ) : (
            <p className="flex items-start gap-2 text-sm text-muted-foreground">
              <Lock className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <span>{t('pollMessage.contentWithheld')}</span>
            </p>
          )}
        </Section>
      </div>
    </>
  );
}
